import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageCircle, Presentation, Search as SearchIcon } from 'lucide-react';
import { Card, CardBody } from '../components/ui';
import {
  listConferenceSessions,
  listConferences,
  listDiscussionRepliesForSearch,
  listDiscussionTopics,
  type DiscussionReplySearchItem
} from '../lib/firestore';
import { formatCompactDateTimeRange, formatExactDateTime, formatRelativeDateTime } from '../lib/dates';
import type { Conference, DiscussionTopic, Session } from '../lib/types';

const MIN_QUERY_LENGTH = 2;
const SEARCH_REPLY_LIMIT = 1000;

type SessionSearchItem = {
  conference: Conference;
  session: Session;
};

type DiscussionSearchResult = {
  key: string;
  title: string;
  to: string;
  snippet: string;
  updatedAt: number;
  score: number;
  kind: 'topic' | 'reply';
};

function compactText(value: unknown) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function searchTerms(query: string) {
  return compactText(query)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

function scoreSearch(query: string, terms: string[], title: string, fields: unknown[]) {
  const normalizedQuery = compactText(query).toLowerCase();
  const normalizedTitle = compactText(title).toLowerCase();
  const haystack = [title, ...fields].map(compactText).join(' ').toLowerCase();

  if (!terms.every((term) => haystack.includes(term))) return 0;

  let score = 1;
  if (normalizedTitle.includes(normalizedQuery)) score += 20;
  if (haystack.includes(normalizedQuery)) score += 5;

  for (const term of terms) {
    if (normalizedTitle.includes(term)) score += 5;
    score += haystack.split(term).length - 1;
  }

  return score;
}

function makeSnippet(fields: unknown[], terms: string[]) {
  const candidates = fields.map(compactText).filter(Boolean);
  const matched =
    candidates.find((candidate) => terms.some((term) => candidate.toLowerCase().includes(term))) ?? candidates[0] ?? '';

  if (!matched) return '';
  if (matched.length <= 180) return matched;

  const lower = matched.toLowerCase();
  const firstMatch = terms
    .map((term) => lower.indexOf(term))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0] ?? 0;
  const start = Math.max(0, firstMatch - 70);
  const end = Math.min(matched.length, start + 180);
  return `${start > 0 ? '...' : ''}${matched.slice(start, end).trim()}${end < matched.length ? '...' : ''}`;
}

function presenterText(session: Session) {
  return (session.presenters ?? [])
    .map((presenter) => compactText(presenter?.name))
    .filter(Boolean)
    .join(', ');
}

function sponsorText(session: Session) {
  return (session.sponsorGroups ?? [])
    .flatMap((group) => [
      group.tier,
      ...group.sponsors.flatMap((sponsor) => [sponsor.name, sponsor.summary, sponsor.detail])
    ])
    .map(compactText)
    .filter(Boolean)
    .join(' ');
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = compactText(searchParams.get('q'));
  const terms = React.useMemo(() => searchTerms(query), [query]);
  const canSearch = query.length >= MIN_QUERY_LENGTH;
  const [topics, setTopics] = React.useState<DiscussionTopic[]>([]);
  const [replies, setReplies] = React.useState<DiscussionReplySearchItem[]>([]);
  const [sessions, setSessions] = React.useState<SessionSearchItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!canSearch) {
      setTopics([]);
      setReplies([]);
      setSessions([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      const [topicsResult, repliesResult, conferencesResult] = await Promise.allSettled([
        listDiscussionTopics(),
        listDiscussionRepliesForSearch(SEARCH_REPLY_LIMIT),
        listConferences()
      ]);
      if (topicsResult.status === 'rejected') throw topicsResult.reason;
      if (conferencesResult.status === 'rejected') throw conferencesResult.reason;

      const nextTopics = topicsResult.value;
      const nextReplies = repliesResult.status === 'fulfilled' ? repliesResult.value : [];
      if (repliesResult.status === 'rejected') {
        console.warn('Discussion reply search could not be loaded.', repliesResult.reason);
      }
      const conferences = conferencesResult.value;
      const conference = conferences[0] ?? null;
      const nextSessions = conference
        ? (await listConferenceSessions(conference.id)).map((session) => ({ conference, session }))
        : [];
      return { nextTopics, nextReplies, nextSessions };
    })()
      .then(({ nextTopics, nextReplies, nextSessions }) => {
        if (cancelled) return;
        setTopics(nextTopics);
        setReplies(nextReplies);
        setSessions(nextSessions);
      })
      .catch((searchError) => {
        if (cancelled) return;
        console.error(searchError);
        setTopics([]);
        setReplies([]);
        setSessions([]);
        setError('Search could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canSearch]);

  const topicById = React.useMemo(() => new Map(topics.map((topic) => [topic.id, topic])), [topics]);

  const discussionResults = React.useMemo(() => {
    if (!canSearch) return [];
    const topicResults = topics
      .map<DiscussionSearchResult | null>((topic) => {
        const score = scoreSearch(query, terms, topic.title, [topic.body, topic.authorDisplayName]);
        return score
          ? {
              key: `topic:${topic.id}`,
              title: topic.title,
              to: `/discussion/${topic.id}`,
              score,
              snippet: makeSnippet([topic.body], terms),
              updatedAt: topic.updatedAt || topic.lastActivityAt || topic.createdAt || 0,
              kind: 'topic'
            }
          : null;
      })
      .filter((result): result is DiscussionSearchResult => Boolean(result));

    const replyResults = replies
      .map<DiscussionSearchResult | null>(({ reply, topicId }) => {
        const topic = topicById.get(topicId);
        const score = scoreSearch(query, terms, topic?.title ?? 'Reply', [
          reply.body,
          reply.authorDisplayName,
          topic?.title
        ]);
        return score
          ? {
              key: `reply:${topicId}:${reply.id}`,
              title: topic ? `Reply in ${topic.title}` : 'Reply',
              to: `/discussion/${topicId}`,
              score,
              snippet: makeSnippet([reply.body], terms),
              updatedAt: reply.updatedAt || reply.createdAt || 0,
              kind: 'reply'
            }
          : null;
      })
      .filter((result): result is DiscussionSearchResult => Boolean(result));

    return [...topicResults, ...replyResults].sort((a, b) => b.score - a.score || b.updatedAt - a.updatedAt);
  }, [canSearch, query, replies, terms, topicById, topics]);

  const sessionResults = React.useMemo(() => {
    if (!canSearch) return [];
    return sessions
      .map(({ conference, session }) => {
        const presenters = presenterText(session);
        const sponsors = sponsorText(session);
        const score = scoreSearch(query, terms, session.title, [
          session.abstract,
          session.agendaAbstract,
          presenters,
          sponsors,
          session.room,
          conference.name,
        ]);
        return score
          ? {
              conference,
              session,
              presenters,
              score,
              snippet: makeSnippet([session.agendaAbstract, session.abstract, sponsors], terms),
            }
          : null;
      })
      .filter(
        (result): result is {
          conference: Conference;
          session: Session;
          presenters: string;
          score: number;
          snippet: string;
        } => Boolean(result)
      )
      .sort((a, b) => b.score - a.score || (a.session.startAt || 0) - (b.session.startAt || 0));
  }, [canSearch, query, sessions, terms]);

  const resultCount = discussionResults.length + sessionResults.length;
  const searchStatus = canSearch
    ? loading
      ? 'Searching...'
      : `${resultCount} result${resultCount === 1 ? '' : 's'} for "${query}"`
    : null;
  const showSearchBody = !query || (query && !canSearch) || Boolean(error);

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card>
        <div className="flex items-center gap-3 border-b border-zinc-300 bg-zinc-200 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <SearchIcon className="h-4 w-4 shrink-0 text-zinc-700" aria-hidden />
            <div className="min-w-0 truncate text-sm font-semibold text-zinc-900">Search</div>
          </div>
          {searchStatus ? <div className="min-w-0 truncate text-xs font-medium text-zinc-700 sm:text-sm">{searchStatus}</div> : null}
        </div>
        {showSearchBody ? (
          <CardBody className="space-y-3">
            {!query ? (
              <div className="text-sm text-zinc-600">Use `/search?q=term` to search discussions and conference sessions.</div>
            ) : null}

            {query && !canSearch ? (
              <div className="text-sm text-zinc-600">Search for at least {MIN_QUERY_LENGTH} characters.</div>
            ) : null}

            {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-2 text-sm text-red-800">{error}</div> : null}
          </CardBody>
        ) : null}
      </Card>

      {canSearch && !loading && !error ? (
        <>
          <Card>
            <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-3">
              <MessageCircle className="h-4 w-4 text-zinc-700" aria-hidden />
              <div className="text-sm font-semibold text-zinc-900">Discussions ({discussionResults.length})</div>
            </div>
            <CardBody className="space-y-3">
              {discussionResults.map((result) => (
                <Link
                  key={result.key}
                  to={result.to}
                  className="block rounded-xl border border-zinc-200 p-3 transition hover:bg-zinc-50"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="break-words text-sm font-semibold text-zinc-900">{result.title}</div>
                    {result.kind === 'reply' ? (
                      <span className="rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700">
                        Reply
                      </span>
                    ) : null}
                  </div>
                  {result.snippet ? <div className="mt-1 line-clamp-2 text-sm text-zinc-700">{result.snippet}</div> : null}
                  <div className="mt-2 text-xs text-zinc-600" title={formatExactDateTime(result.updatedAt)}>
                    Updated {formatRelativeDateTime(result.updatedAt)}
                  </div>
                </Link>
              ))}
              {discussionResults.length === 0 ? <div className="text-sm text-zinc-600">No discussion matches.</div> : null}
            </CardBody>
          </Card>

          <Card>
            <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-3">
              <Presentation className="h-4 w-4 text-zinc-700" aria-hidden />
              <div className="text-sm font-semibold text-zinc-900">Conference Sessions ({sessionResults.length})</div>
            </div>
            <CardBody className="space-y-3">
              {sessionResults.map(({ conference, session, presenters, snippet }) => (
                <Link
                  key={`${conference.id}:${session.id}`}
                  to={`/conference?session=${encodeURIComponent(session.id)}`}
                  className="block rounded-xl border border-zinc-200 p-3 transition hover:bg-zinc-50"
                >
                  <div className="break-words text-sm font-semibold text-zinc-900">{session.title}</div>
                  {snippet ? <div className="mt-1 line-clamp-2 text-sm text-zinc-700">{snippet}</div> : null}
                  <div className="mt-2 text-xs text-zinc-600">
                    {conference.name} | {formatCompactDateTimeRange(session.startAt, session.endAt)}
                    {session.room ? ` | ${session.room}` : ''}
                    {presenters ? ` | ${presenters}` : ''}
                  </div>
                </Link>
              ))}
              {sessionResults.length === 0 ? <div className="text-sm text-zinc-600">No conference session matches.</div> : null}
            </CardBody>
          </Card>
        </>
      ) : null}
    </div>
  );
}
